import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	fitnessProfileDateFieldShellClassName,
	fitnessProfileDateInputClassName,
	fitnessProfileFormErrorClassName,
	fitnessProfileSurfaceCardClassName
} from '../../FitnessProfile/shared/fitnessProfileReadingStyles';
import PageTitle from '../../FormElements/PageTitle';
import { useAuth } from '../../../contexts/AuthContext';
import useSubscriptionGate from '../../../hooks/useSubscriptionGate';
import { replaceFitnessProfileEndpoint } from '../../../utils/fitnessProfileBloodPressure';
import { fetchUserFitnessProfiles } from '../../../utils/fitnessProfilesApi';
import { replaceUserEndpoint } from '../../../utils/userEndpoint';
import {
	GOAL_CATEGORY_UNSET,
	isGoalCategorySelected,
	labelForGoalCategory,
	validateGoalRows
} from '../../../constants/fitnessProfileGoals';
import {
	accountOwnerProfileIdentity,
	buildCompleteIntakePayload,
	buildCreateFitnessProfilePayload,
	buildInviteOnlyFitnessProfilePayload,
	computeAgeFromDateOfBirth,
	DIET_PATTERN_OPTIONS,
	EXERCISE_FREQUENCY_OPTIONS,
	EXERCISE_INTENSITY_OPTIONS,
	GOAL_CATEGORY_OPTIONS,
	labelForOption
} from '../../../constants/fitnessProfileQuestionnaire';
import HeightInput from '../../FitnessProfile/HeightInput';
import FitnessHubsOnlyToggle from '../../FitnessProfile/shared/FitnessHubsOnlyToggle';
import { formatHeightDisplay, parseHeightDisplay } from '../../../utils/heightDisplay';

const FULL_INTAKE_STEPS = [
	{ id: 'basic', label: 'Basic Profile' },
	{ id: 'habits', label: 'Current Habits' },
	{ id: 'equipment_settings', label: 'Equipment & Settings' },
	{ id: 'goals', label: 'Goals & Timeline' },
	{ id: 'review', label: 'Review' }
];

const PRIMARY_CREATE_STEPS = [
	{ id: 'identity', label: 'Name & Email' },
	{ id: 'basic', label: 'Basics' },
	{ id: 'habits', label: 'Current Habits' },
	{ id: 'equipment_settings', label: 'Equipment & Settings' },
	{ id: 'goals', label: 'Goals & Timeline' },
	{ id: 'review', label: 'Review' }
];

const INVITE_STEPS = [{ id: 'invite', label: 'Invite Member' }];

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

function emptyGoal() {
	return { outcome: '', targetMetric: '', targetDate: '', category: GOAL_CATEGORY_UNSET };
}

function emptyCreateFormData({ userEmail = '', userName = '', isFirstProfile = false } = {}) {
	return {
		name: isFirstProfile ? userName : '',
		email: isFirstProfile ? userEmail : '',
		dateOfBirth: '',
		height: '',
		weight: '',
		bloodPressure: '',
		dietPattern: '',
		dietNotes: '',
		exerciseFrequency: '',
		exerciseIntensity: '',
		sleepHours: '',
		habitNotes: '',
		equipmentAccess: '',
		fitnessHubsOnly: !isFirstProfile,
		isPrimary: isFirstProfile,
		goals: []
	};
}

function SectionCard({ title, subtitle, children }) {
	return (
		<section className={`${fitnessProfileSurfaceCardClassName} px-5 py-6 md:px-7`}>
			<div className="mb-5">
				<h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-dark-text">
					{title}
				</h2>
				{subtitle && <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
			</div>
			{children}
		</section>
	);
}

function Field({ id, label, required = false, hint, error, children }) {
	return (
		<label htmlFor={id} className="block text-sm">
			<span className="font-medium text-gray-700 dark:text-dark-text">
				{label}
				{required && <span className="text-red-600 dark:text-red-400"> *</span>}
			</span>
			<div className="mt-1.5">{children}</div>
			{hint && <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
			{error && (
				<p id={`${id}-error`} className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400">
					{error}
				</p>
			)}
		</label>
	);
}

/** 16px minimum avoids iOS Safari auto-zoom on focus (same as Challenge Wire forms). */
function inputClasses(hasError, { readOnly = false } = {}) {
	return `w-full min-w-0 max-w-full box-border rounded-xl border px-3.5 py-3 text-base text-gray-900 dark:text-dark-text outline-none transition placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:[color-scheme:dark] ${
		readOnly
			? 'cursor-default border-gray-200 bg-gray-50 focus:ring-0 dark:border-dark-border dark:bg-dark-card'
			: `bg-white dark:bg-dark-bg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 ${
					hasError
						? 'border-red-400 dark:border-red-500 focus:border-red-500 dark:focus:border-red-400'
						: 'border-gray-300 dark:border-dark-border focus:border-blue-500 dark:focus:border-blue-400'
				}`
	}`;
}

export default function FitnessProfileQuestionnairePage({ mode = 'create' }) {
	const navigate = useNavigate();
	const location = useLocation();
	const { id: profileIdParam } = useParams();
	const { user, csrfToken, endpoints, syncPushSubscription, refreshSessionPreferences, patchActiveFitnessProfile } = useAuth();
	const { isSubscriptionRequired, openPricingModal } = useSubscriptionGate();
	const isCompleteMode = mode === 'complete';
	const [currentStep, setCurrentStep] = useState(0);
	const [errors, setErrors] = useState({});
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [submitError, setSubmitError] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isHouseholdProfileCreate, setIsHouseholdProfileCreate] = useState(false);
	const [isFirstProfileCreate, setIsFirstProfileCreate] = useState(false);
	const [isLoadingCreateMode, setIsLoadingCreateMode] = useState(!isCompleteMode);
	const [isLoadingProfile, setIsLoadingProfile] = useState(isCompleteMode);
	const [loadProfileError, setLoadProfileError] = useState('');
	const [formData, setFormData] = useState({
		name: '',
		email: user?.email || '',
		dateOfBirth: '',
		height: '',
		weight: '',
		bloodPressure: '',
		dietPattern: '',
		dietNotes: '',
		exerciseFrequency: '',
		exerciseIntensity: '',
		sleepHours: '',
		habitNotes: '',
		equipmentAccess: '',
		fitnessHubsOnly: false,
		isPrimary: false,
		goals: []
	});

	useEffect(() => {
		if (!isFirstProfileCreate || !user || isLoadingCreateMode) return;

		const accountIdentity = accountOwnerProfileIdentity(user);
		setFormData((previous) => ({
			...previous,
			name: accountIdentity.name,
			email: accountIdentity.email
		}));
	}, [
		isFirstProfileCreate,
		user,
		isLoadingCreateMode
	]);

	useEffect(() => {
		if (!isSubscriptionRequired) return;

		openPricingModal();
		navigate('/fitness-profiles', { replace: true });
	}, [isSubscriptionRequired, navigate, openPricingModal]);

	useEffect(() => {
		window.requestAnimationFrame(() => {
			window.scrollTo({ top: 0, left: 0 });
		});
	}, [currentStep]);

	useEffect(() => {
		if (isCompleteMode || !user?.id || !endpoints?.get_user_fitness_profiles_endpoint) {
			setIsLoadingCreateMode(false);
			return;
		}

		let cancelled = false;

		async function loadCreateMode() {
			setIsLoadingCreateMode(true);
			setCurrentStep(0);
			setErrors({});
			setSubmitError('');
			setIsSubmitted(false);

			try {
				const existingProfiles = await fetchUserFitnessProfiles({
					userId: user.id,
					endpoint: endpoints.get_user_fitness_profiles_endpoint,
					csrfToken
				});
				if (cancelled) return;

				const isFirstProfile = existingProfiles.length === 0;

				const accountIdentity = accountOwnerProfileIdentity(user);

				setIsHouseholdProfileCreate(!isFirstProfile);
				setIsFirstProfileCreate(isFirstProfile);
				setFormData(emptyCreateFormData({
					userEmail: accountIdentity.email,
					userName: accountIdentity.name,
					isFirstProfile
				}));
			} catch (_error) {
				if (!cancelled) {
					setSubmitError('Could not determine whether this is a primary or household profile. Please try again.');
					setIsHouseholdProfileCreate(false);
					setIsFirstProfileCreate(false);
				}
			} finally {
				if (!cancelled) {
					setIsLoadingCreateMode(false);
				}
			}
		}

		loadCreateMode();

		return () => {
			cancelled = true;
		};
	}, [
		isCompleteMode,
		location.key,
		location.pathname,
		user?.id,
		csrfToken,
		endpoints?.get_user_fitness_profiles_endpoint
	]);

	useEffect(() => {
		if (!isCompleteMode || !user?.id || !profileIdParam || !endpoints?.get_user_fitness_profile_endpoint) {
			return;
		}

		let cancelled = false;

		async function loadProfile() {
			setIsLoadingProfile(true);
			setLoadProfileError('');

			try {
				const response = await fetch(
					replaceFitnessProfileEndpoint(endpoints.get_user_fitness_profile_endpoint, {
						userId: user.id,
						fitnessProfileId: profileIdParam
					}),
					{
						headers: { 'Content-Type': 'application/json' },
						credentials: 'same-origin'
					}
				);
				const body = await response.json();
				if (cancelled) return;

				if (!response.ok || !body.success) {
					throw new Error(body.error || 'Could not load your profile.');
				}

				if (body.data.intake_complete) {
					navigate('/fitness-hub', { replace: true });
					return;
				}

				setFormData((previous) => ({
					...previous,
					name: body.data.name || '',
					email: body.data.email || ''
				}));
			} catch (error) {
				if (!cancelled) {
					setLoadProfileError(error.message || 'Could not load your profile.');
				}
			} finally {
				if (!cancelled) {
					setIsLoadingProfile(false);
				}
			}
		}

		loadProfile();

		return () => {
			cancelled = true;
		};
	}, [
		endpoints?.get_user_fitness_profile_endpoint,
		isCompleteMode,
		navigate,
		profileIdParam,
		user?.id
	]);

	const isInviteOnlyCreate = isHouseholdProfileCreate && !isCompleteMode;
	const usesSplitIdentityStep = !isCompleteMode && !isInviteOnlyCreate;
	const STEPS = isCompleteMode
		? FULL_INTAKE_STEPS
		: isInviteOnlyCreate
			? INVITE_STEPS
			: PRIMARY_CREATE_STEPS;

	const computedAge = useMemo(
		() => computeAgeFromDateOfBirth(formData.dateOfBirth),
		[formData.dateOfBirth]
	);

	const progress = useMemo(() => Math.round(((currentStep + 1) / STEPS.length) * 100), [currentStep]);
	const stepId = STEPS[currentStep].id;
	const isReviewStep = stepId === 'review';

	function onInputChange(event) {
		const { name, value } = event.target;
		setFormData((previous) => ({ ...previous, [name]: value }));
	}

	function onGoalChange(index, field, value) {
		setFormData((previous) => {
			const goals = [...previous.goals];
			goals[index] = { ...goals[index], [field]: value };
			return { ...previous, goals };
		});
	}

	function addGoal() {
		setFormData((previous) => ({ ...previous, goals: [...previous.goals, emptyGoal()] }));
	}

	function removeGoal(index) {
		setFormData((previous) => ({
			...previous,
			goals: previous.goals.filter((_, goalIndex) => goalIndex !== index)
		}));
	}

	function validateIdentityFields() {
		const nextErrors = {};
		if (!formData.name.trim()) nextErrors.name = 'Please add a name.';
		if (!formData.email.trim()) {
			nextErrors.email = 'Email is required.';
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
			nextErrors.email = 'Enter a valid email address.';
		}
		return nextErrors;
	}

	function validateInviteStep() {
		return validateIdentityFields();
	}

	function validateIdentityStep() {
		return validateIdentityFields();
	}

	function validateBasicStep() {
		const nextErrors = {};
		const skipNameEmail = isCompleteMode || usesSplitIdentityStep;
		if (!skipNameEmail) {
			if (!formData.name.trim()) nextErrors.name = 'Please add a name.';
			if (!formData.email.trim()) {
				nextErrors.email = 'Email is required for challenge reminders.';
			} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
				nextErrors.email = 'Enter a valid email address.';
			}
		}
		if (!formData.dateOfBirth) {
			nextErrors.dateOfBirth = 'Date of birth is required.';
		} else if (computedAge == null || computedAge < 13 || computedAge > 120) {
			nextErrors.dateOfBirth = 'Age must be between 13 and 120.';
		}
		if (!formatHeightDisplay(parseHeightDisplay(formData.height))) {
			nextErrors.height = 'Choose your height.';
		}
		const weight = Number(formData.weight);
		if (!formData.weight || !Number.isFinite(weight) || weight <= 0) {
			nextErrors.weight = 'Weight should be a positive number.';
		}
		if (
			formData.bloodPressure.trim() &&
			!/^\d{2,3}\/\d{2,3}$/.test(formData.bloodPressure.trim())
		) {
			nextErrors.bloodPressure = 'Use format like 120/80.';
		}
		return nextErrors;
	}

	function validateHabitsStep() {
		const nextErrors = {};
		if (!formData.dietPattern) nextErrors.dietPattern = 'Choose your current diet pattern.';
		if (!formData.exerciseFrequency) nextErrors.exerciseFrequency = 'Choose activity frequency.';
		if (!formData.exerciseIntensity) nextErrors.exerciseIntensity = 'Choose workout intensity.';
		const sleep = Number(formData.sleepHours);
		if (!formData.sleepHours || !Number.isFinite(sleep) || sleep < 1 || sleep > 14) {
			nextErrors.sleepHours = 'Sleep average should be between 1 and 14 hours.';
		}
		return nextErrors;
	}

	function validateGoalsStep() {
		const weight = Number(formData.weight);
		const hasWeightReading = Number.isFinite(weight) && weight > 0;
		const hasBloodPressureReading = /^\d{2,3}\/\d{2,3}$/.test(formData.bloodPressure.trim());

		return validateGoalRows(formData.goals, {
			today: todayIsoDate(),
			requireAtLeastOne: false,
			hasWeightReading,
			hasBloodPressureReading
		});
	}

	function validateCurrentStep() {
		if (stepId === 'invite') return validateInviteStep();
		if (stepId === 'identity') return validateIdentityStep();
		if (stepId === 'basic') return validateBasicStep();
		if (stepId === 'habits') return validateHabitsStep();
		if (stepId === 'goals') return validateGoalsStep();
		return {};
	}

	function goToPreviousStep() {
		setErrors({});
		setCurrentStep((previous) => Math.max(previous - 1, 0));
	}

	function goToNextStep() {
		const nextErrors = validateCurrentStep();
		setErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) return;
		setCurrentStep((previous) => Math.min(previous + 1, STEPS.length - 1));
	}

	function onStepClick(index) {
		if (index <= currentStep) {
			setErrors({});
			setCurrentStep(index);
		}
	}

	async function handleSubmit(event) {
		event.preventDefault();
		if (!isReviewStep && !isInviteOnlyCreate) return goToNextStep();

		if (isInviteOnlyCreate) {
			const inviteErrors = validateInviteStep();
			if (Object.keys(inviteErrors).length > 0) {
				setErrors(inviteErrors);
				return;
			}
		}

		if (!user?.id) {
			setIsSubmitted(true);
			return;
		}

		if (isCompleteMode) {
			if (!endpoints?.complete_user_fitness_profile_intake_endpoint || !profileIdParam) {
				setSubmitError('Could not complete profile setup.');
				return;
			}

			const goalErrors = validateGoalsStep();
			if (Object.keys(goalErrors).length > 0) {
				setErrors(goalErrors);
				setSubmitError(goalErrors.goals || 'Fix goal errors before saving your profile.');
				return;
			}

			const payload = buildCompleteIntakePayload(formData);
			setIsSubmitting(true);
			setSubmitError('');

			try {
				const url = replaceFitnessProfileEndpoint(endpoints.complete_user_fitness_profile_intake_endpoint, {
					userId: user.id,
					fitnessProfileId: profileIdParam
				});
				const response = await fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken
					},
					body: JSON.stringify({ fitness_profile: payload })
				});
				const data = await response.json();

				if (!response.ok || !data.success) {
					throw new Error((data.errors || []).join(' ') || data.error || 'Could not complete profile.');
				}

				patchActiveFitnessProfile({
					intake_complete: true,
					intake_required: false
				});
				await refreshSessionPreferences();
				navigate('/fitness-hub');
			} catch (error) {
				setSubmitError(error.message || 'Could not complete profile.');
			} finally {
				setIsSubmitting(false);
			}
			return;
		}

		if (!endpoints?.create_user_fitness_profile_endpoint) {
			setIsSubmitted(true);
			return;
		}

		if (!isInviteOnlyCreate) {
			const goalErrors = validateGoalsStep();
			if (Object.keys(goalErrors).length > 0) {
				setErrors(goalErrors);
				setSubmitError(goalErrors.goals || 'Fix goal errors before creating your profile.');
				return;
			}
		}

		const payload = isInviteOnlyCreate
			? buildInviteOnlyFitnessProfilePayload(formData, {
					includeFitnessHubsSetting: true
				})
			: buildCreateFitnessProfilePayload(formData, {
					includeFitnessHubsSetting: isHouseholdProfileCreate,
					setAsPrimary: isFirstProfileCreate || Boolean(formData.isPrimary)
				});

		setIsSubmitting(true);
		setSubmitError('');

		try {
			const url = replaceFitnessProfileEndpoint(endpoints.create_user_fitness_profile_endpoint, {
				userId: user.id,
				fitnessProfileId: '0'
			});
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				body: JSON.stringify({
					fitness_profile: payload
				})
			});
			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error((data.errors || []).join(' ') || data.error || 'Could not create profile.');
			}

			await syncPushSubscription();
			if (isInviteOnlyCreate) {
				const invitedEmail = encodeURIComponent(formData.email.trim());
				navigate(`/fitness-profiles?invite_sent=${invitedEmail}`);
				return;
			}

			navigate(`/fitness-profiles/${data.data.id}`);
		} catch (error) {
			setSubmitError(error.message || 'Could not create profile.');
		} finally {
			setIsSubmitting(false);
		}
	}

	if (isSubscriptionRequired && !isCompleteMode) {
		return null;
	}

	if (!isCompleteMode && isLoadingCreateMode) {
		return (
			<div className="container p-4 pt-4 lg:py-16 md:px-20">
				<p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
			</div>
		);
	}

	if (!isCompleteMode && !isHouseholdProfileCreate && !isFirstProfileCreate) {
		return (
			<div className="container p-4 pt-4 lg:py-16 md:px-20">
				<p className="text-sm text-red-600 dark:text-red-400">
					{submitError || 'Could not load profile setup. Please try again.'}
				</p>
			</div>
		);
	}

	if (isCompleteMode && isLoadingProfile) {
		return (
			<div className="container p-4 pt-4 lg:py-16 md:px-20">
				<p className="text-sm text-gray-500 dark:text-gray-400">Loading your profile…</p>
			</div>
		);
	}

	if (isCompleteMode && loadProfileError) {
		return (
			<div className="container p-4 pt-4 lg:py-16 md:px-20">
				<p className="text-sm text-red-600 dark:text-red-400">{loadProfileError}</p>
			</div>
		);
	}

	return (
		<div className="container p-4 pt-4 lg:py-16 md:px-20">
			<div className="mx-auto min-w-0 max-w-5xl">
				<div className="mt-4 flex items-center justify-between gap-4">
					<PageTitle
						title={
							isCompleteMode
								? 'Complete Your Profile'
								: isInviteOnlyCreate
									? 'Invite Household Member'
									: isFirstProfileCreate
										? 'Create Your Primary Profile'
										: 'Create Fitness Profile'
						}
					/>
					{!isCompleteMode && (
						<Link
							to="/fitness-hub"
							className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
						>
							Back to Fitness Hub
						</Link>
					)}
				</div>

				{isCompleteMode && (
					<p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
						Welcome, {formData.name || 'athlete'}. Complete your baseline profile and habits to start using
						Fantasy Fitness.
					</p>
				)}

				{isFirstProfileCreate && (
					<p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
						Your primary profile uses your account name and email, then you will complete your baseline
						metrics and habits.
					</p>
				)}

				{isInviteOnlyCreate && (
					<p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
						Add a household member with their name and email. They will receive an invitation to set up
						their login and complete their own profile.
					</p>
				)}

				<div className={`mt-5 ${fitnessProfileSurfaceCardClassName} p-4 md:p-5`}>
					<div className="flex items-center justify-between">
						<p className="text-sm text-gray-500 dark:text-gray-400">
							Step {currentStep + 1} of {STEPS.length}
						</p>
						<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">{progress}%</p>
					</div>
					<div className="mt-3 h-1.5 rounded-full bg-gray-200 dark:bg-neutral-800 overflow-hidden">
						<div
							className="h-full rounded-full bg-blue-600 dark:bg-blue-500 transition-all duration-300"
							style={{ width: `${progress}%` }}
						/>
					</div>
					<div
						className={`mt-4 grid grid-cols-2 gap-2 ${
							STEPS.length > 5 ? 'md:grid-cols-3' : 'md:grid-cols-4'
						}`}
					>
						{STEPS.map((step, index) => {
							const isActive = index === currentStep;
							const isComplete = index < currentStep;
							return (
								<button
									key={step.id}
									type="button"
									onClick={() => onStepClick(index)}
									className={`rounded-xl border px-3 py-2.5 text-left text-xs font-medium transition ${
										isActive
											? 'border-gray-900 bg-gray-900 text-white dark:border-blue-500 dark:bg-blue-600 dark:text-white'
											: isComplete
												? 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg text-gray-700 dark:text-dark-text hover:border-gray-400 dark:hover:border-gray-500'
												: 'border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card text-gray-400 dark:text-gray-500'
									}`}
									disabled={index > currentStep}
								>
									{index + 1}. {step.label}
								</button>
							);
						})}
					</div>
				</div>

				{isSubmitted ? (
					<div className="mt-6">
						<SectionCard
							title="Profile Created"
							subtitle="Your fitness profile, habits, and goals are saved."
						>
							<div className="rounded-xl border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900 dark:bg-opacity-25 p-4">
								<p className="text-sm text-emerald-800 dark:text-emerald-200">
									Excellent. {formData.name || 'Your athlete'} now has a complete intake foundation for
									goal-based training and nutrition planning.
								</p>
							</div>
							<div className="mt-5 flex flex-wrap gap-3">
								<Link
									to="/fitness-profiles/new"
									className="inline-flex items-center justify-center rounded-xl border border-gray-300 dark:border-dark-border px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-bg"
								>
									Create Another Profile
								</Link>
								<Link
									to="/fitness-profiles"
									className="inline-flex items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500"
								>
									View Profiles
								</Link>
							</div>
						</SectionCard>
					</div>
				) : (
					<form onSubmit={handleSubmit} className="mt-6 min-w-0 space-y-5 pb-5">
						{stepId === 'identity' && (
							<SectionCard
								title="Primary profile"
								subtitle="This will be your main fitness profile on this account."
							>
								<div className="mb-5 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 px-4 py-3 text-sm text-blue-900 dark:text-blue-100">
									Your first profile is automatically set as primary.
								</div>
								<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
									<Field
										id="name"
										label="Name"
										required
										error={errors.name}
										hint="Uses your account first name. Update your user profile to change."
									>
										<input
											id="name"
											name="name"
											value={formData.name}
											readOnly
											placeholder="Jordan Smith"
											className={inputClasses(Boolean(errors.name), { readOnly: true })}
											aria-invalid={Boolean(errors.name)}
											aria-describedby={errors.name ? 'name-error' : undefined}
										/>
									</Field>
									<Field
										id="email"
										label="Email"
										required
										error={errors.email}
										hint="Inherited from your account. Used for challenge reminders."
									>
										<input
											id="email"
											type="email"
											name="email"
											value={formData.email}
											readOnly
											placeholder="name@example.com"
											className={inputClasses(Boolean(errors.email), { readOnly: true })}
											aria-invalid={Boolean(errors.email)}
											aria-describedby={errors.email ? 'email-error' : undefined}
										/>
									</Field>
								</div>
							</SectionCard>
						)}

						{stepId === 'invite' && (
							<SectionCard
								title="Household member"
								subtitle="They will complete height, weight, habits, and goals after accepting the invite."
							>
								<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
									<Field id="name" label="Name" required error={errors.name}>
										<input
											id="name"
											name="name"
											value={formData.name}
											onChange={onInputChange}
											placeholder="Jordan Smith"
											className={inputClasses(Boolean(errors.name))}
											aria-invalid={Boolean(errors.name)}
											aria-describedby={errors.name ? 'name-error' : undefined}
										/>
									</Field>
									<Field
										id="email"
										label="Email"
										required
										error={errors.email}
										hint="Must be unique across Fantasy Fitness and cannot match an existing account login. An invitation will be sent to this address."
									>
										<input
											id="email"
											type="email"
											name="email"
											value={formData.email}
											onChange={onInputChange}
											placeholder="name@example.com"
											className={inputClasses(Boolean(errors.email))}
											aria-invalid={Boolean(errors.email)}
											aria-describedby={errors.email ? 'email-error' : undefined}
										/>
									</Field>
									<div className="md:col-span-2">
										<FitnessHubsOnlyToggle
											checked={formData.fitnessHubsOnly}
											onChange={(checked) =>
												setFormData((previous) => ({ ...previous, fitnessHubsOnly: checked }))
											}
										/>
									</div>
								</div>
							</SectionCard>
						)}

						{stepId === 'basic' && (
							<SectionCard
								title="Baseline metrics"
								subtitle="Record height, weight, and date of birth to define your starting point."
							>
								{(isCompleteMode || usesSplitIdentityStep) && (
									<div className="mb-5 rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg px-4 py-3 text-sm text-gray-700 dark:text-dark-text">
										<p>
											<span className="font-medium">Name:</span> {formData.name}
										</p>
										<p className="mt-1">
											<span className="font-medium">Email:</span> {formData.email}
										</p>
									</div>
								)}
								<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
									{!isCompleteMode && !usesSplitIdentityStep && (
										<>
											<Field id="name" label="Name" required error={errors.name}>
												<input
													id="name"
													name="name"
													value={formData.name}
													onChange={onInputChange}
													placeholder="Jordan Smith"
													className={inputClasses(Boolean(errors.name))}
													aria-invalid={Boolean(errors.name)}
													aria-describedby={errors.name ? 'name-error' : undefined}
												/>
											</Field>
											<Field
												id="email"
												label="Email"
												required
												error={errors.email}
												hint="Defaults to your account email. Must be unique across Fantasy Fitness if you use a different address."
											>
												<input
													id="email"
													type="email"
													name="email"
													value={formData.email}
													onChange={onInputChange}
													placeholder="name@example.com"
													className={inputClasses(Boolean(errors.email))}
													aria-invalid={Boolean(errors.email)}
													aria-describedby={errors.email ? 'email-error' : undefined}
												/>
											</Field>
										</>
									)}
									<Field
										id="dateOfBirth"
										label="Date of birth"
										required
										hint={computedAge != null ? `Age ${computedAge}` : undefined}
										error={errors.dateOfBirth}
									>
										<div className="min-w-0 w-full">
											<div
												className={fitnessProfileDateFieldShellClassName(
													Boolean(errors.dateOfBirth)
												)}
											>
												<input
													id="dateOfBirth"
													type="date"
													name="dateOfBirth"
													value={formData.dateOfBirth}
													onChange={onInputChange}
													className={fitnessProfileDateInputClassName}
													aria-invalid={Boolean(errors.dateOfBirth)}
													aria-describedby={errors.dateOfBirth ? 'dateOfBirth-error' : undefined}
												/>
											</div>
										</div>
									</Field>
									<Field
										id="height"
										label="Height"
										required
										hint="Use the dropdowns — no need to type feet or inch marks."
										error={errors.height}
									>
										<HeightInput
											id="height"
											value={formData.height}
											onChange={(display) =>
												setFormData((prev) => ({ ...prev, height: display }))
											}
											inputClassName={inputClasses(Boolean(errors.height))}
											hasError={Boolean(errors.height)}
										/>
									</Field>
									<Field id="weight" label="Weight (lbs)" required error={errors.weight}>
										<input
											id="weight"
											type="number"
											name="weight"
											value={formData.weight}
											onChange={onInputChange}
											placeholder="192"
											min="1"
											className={inputClasses(Boolean(errors.weight))}
											aria-invalid={Boolean(errors.weight)}
											aria-describedby={errors.weight ? 'weight-error' : undefined}
										/>
									</Field>
									<div className="md:col-span-2">
										<Field
											id="bloodPressure"
											label="Blood Pressure (optional)"
											hint="Use systolic/diastolic if available."
											error={errors.bloodPressure}
										>
											<input
												id="bloodPressure"
												name="bloodPressure"
												value={formData.bloodPressure}
												onChange={onInputChange}
												placeholder="120/80"
												className={inputClasses(Boolean(errors.bloodPressure))}
												aria-invalid={Boolean(errors.bloodPressure)}
												aria-describedby={
													errors.bloodPressure ? 'bloodPressure-error' : undefined
												}
											/>
										</Field>
									</div>
								</div>
							</SectionCard>
						)}

						{stepId === 'habits' && (
							<SectionCard
								title="Current Habits"
								subtitle="Capture the current diet and training reality."
							>
								<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
									<Field id="dietPattern" label="Current diet pattern" required error={errors.dietPattern}>
										<select
											id="dietPattern"
											name="dietPattern"
											value={formData.dietPattern}
											onChange={onInputChange}
											className={inputClasses(Boolean(errors.dietPattern))}
											aria-invalid={Boolean(errors.dietPattern)}
											aria-describedby={errors.dietPattern ? 'dietPattern-error' : undefined}
										>
											<option value="">Select an option</option>
											{DIET_PATTERN_OPTIONS.map((option) => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</Field>
									<Field
										id="exerciseFrequency"
										label="Exercise frequency"
										required
										error={errors.exerciseFrequency}
									>
										<select
											id="exerciseFrequency"
											name="exerciseFrequency"
											value={formData.exerciseFrequency}
											onChange={onInputChange}
											className={inputClasses(Boolean(errors.exerciseFrequency))}
											aria-invalid={Boolean(errors.exerciseFrequency)}
											aria-describedby={
												errors.exerciseFrequency ? 'exerciseFrequency-error' : undefined
											}
										>
											<option value="">Select an option</option>
											{EXERCISE_FREQUENCY_OPTIONS.map((option) => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</Field>
									<Field
										id="exerciseIntensity"
										label="Exercise intensity"
										required
										error={errors.exerciseIntensity}
									>
										<select
											id="exerciseIntensity"
											name="exerciseIntensity"
											value={formData.exerciseIntensity}
											onChange={onInputChange}
											className={inputClasses(Boolean(errors.exerciseIntensity))}
											aria-invalid={Boolean(errors.exerciseIntensity)}
											aria-describedby={
												errors.exerciseIntensity ? 'exerciseIntensity-error' : undefined
											}
										>
											<option value="">Select an option</option>
											{EXERCISE_INTENSITY_OPTIONS.map((option) => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</Field>
									<Field id="sleepHours" label="Average sleep (hours)" required error={errors.sleepHours}>
										<input
											id="sleepHours"
											type="number"
											name="sleepHours"
											value={formData.sleepHours}
											onChange={onInputChange}
											placeholder="7.5"
											min="1"
											max="14"
											step="0.1"
											className={inputClasses(Boolean(errors.sleepHours))}
											aria-invalid={Boolean(errors.sleepHours)}
											aria-describedby={errors.sleepHours ? 'sleepHours-error' : undefined}
										/>
									</Field>
									<div className="md:col-span-2">
										<Field
											id="dietNotes"
											label="Diet notes (optional)"
											hint="Meal timing, cravings, or dietary constraints."
										>
											<textarea
												id="dietNotes"
												name="dietNotes"
												value={formData.dietNotes}
												onChange={onInputChange}
												placeholder="Usually balanced breakfast, larger dinner, occasional late-night snacks."
												rows={3}
												className={inputClasses(false)}
											/>
										</Field>
									</div>
									<div className="md:col-span-2">
										<Field
											id="habitNotes"
											label="Training notes (optional)"
											hint="Injuries, preferred split, schedule constraints."
										>
											<textarea
												id="habitNotes"
												name="habitNotes"
												value={formData.habitNotes}
												onChange={onInputChange}
												placeholder="Can train 4 mornings/week. Knee discomfort after long runs."
												rows={3}
												className={inputClasses(false)}
											/>
										</Field>
									</div>
								</div>
							</SectionCard>
						)}

						{stepId === 'equipment_settings' && (
							<div className="space-y-5">
								<SectionCard
									title="Equipment access"
									subtitle="Tell us what you can train with so recommendations stay realistic."
								>
									<Field
										id="equipmentAccess"
										label="Available equipment"
										hint="List gym access, free weights, resistance bands, machines, or home setup details."
									>
										<textarea
											id="equipmentAccess"
											name="equipmentAccess"
											value={formData.equipmentAccess}
											onChange={onInputChange}
											placeholder="Planet Fitness membership, adjustable dumbbells, resistance bands, treadmill."
											rows={5}
											className={inputClasses(false)}
										/>
									</Field>
								</SectionCard>

								<SectionCard
									title="Profile settings"
									subtitle="Choose how much of the app this household profile should see."
								>
									{isHouseholdProfileCreate ? (
										<FitnessHubsOnlyToggle
											checked={formData.fitnessHubsOnly}
											onChange={(value) =>
												setFormData((previous) => ({
													...previous,
													fitnessHubsOnly: value
												}))}
										/>
									) : (
										<p className="text-sm text-gray-600 dark:text-dark-text-secondary">
											Your primary profile always has access to all hubs. Add a household
											profile to enable a fitness-focused view.
										</p>
									)}
								</SectionCard>
							</div>
						)}

						{stepId === 'goals' && (
							<SectionCard
								title="Goals & Timeline"
								subtitle="Optional — set meaningful outcomes with a real timeline."
							>
								{errors.goals && (
									<div className={`mb-4 ${fitnessProfileFormErrorClassName}`}>
										{errors.goals}
									</div>
								)}

								<div className="space-y-4">
									{formData.goals.map((goal, index) => (
										<div
											key={`goal-${index + 1}`}
											className="rounded-xl border border-gray-300 dark:border-dark-border bg-gray-50 dark:bg-dark-bg p-4"
										>
											<div className="flex items-center justify-between">
												<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
													Goal {index + 1}
												</p>
												<button
													type="button"
													onClick={() => removeGoal(index)}
													className="text-xs font-medium text-red-600 dark:text-red-400"
												>
													Remove
												</button>
											</div>
											<div className="mt-3 space-y-4">
												<Field
													id={`goal-category-${index}`}
													label="Category"
													required
													error={errors[`goal_category_${index}`]}
												>
													<select
														id={`goal-category-${index}`}
														value={
															isGoalCategorySelected(goal.category)
																? goal.category
																: GOAL_CATEGORY_UNSET
														}
														onChange={(event) =>
															onGoalChange(index, 'category', event.target.value)
														}
														className={inputClasses(Boolean(errors[`goal_category_${index}`]))}
													>
														<option value={GOAL_CATEGORY_UNSET}>Select a category...</option>
														{GOAL_CATEGORY_OPTIONS.map((option) => (
															<option key={option.value} value={option.value}>
																{option.label}
															</option>
														))}
													</select>
												</Field>
												<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
												<Field
													id={`goal-outcome-${index}`}
													label="Desired outcome"
													required
													error={errors[`goal_outcome_${index}`]}
												>
													<input
														id={`goal-outcome-${index}`}
														value={goal.outcome}
														onChange={(event) =>
															onGoalChange(index, 'outcome', event.target.value)
														}
														placeholder="Lose 15 lbs by summer"
														className={inputClasses(Boolean(errors[`goal_outcome_${index}`]))}
													/>
												</Field>
												<Field
													id={`goal-target-${index}`}
													label="Target metric"
													required
													error={errors[`goal_target_${index}`]}
												>
													<input
														id={`goal-target-${index}`}
														value={goal.targetMetric}
														onChange={(event) =>
															onGoalChange(index, 'targetMetric', event.target.value)
														}
														placeholder={
															goal.category === 'weight_loss' || goal.category === 'weight_gain'
																? '175 lbs'
																: goal.category === 'blood_pressure'
																	? '120/80'
																	: '3 workouts / week'
														}
														className={inputClasses(Boolean(errors[`goal_target_${index}`]))}
													/>
												</Field>
												<Field
													id={`goal-target-date-${index}`}
													label="Target date"
													required
													error={errors[`goal_target_date_${index}`]}
												>
													<div className="min-w-0 w-full">
														<div
															className={fitnessProfileDateFieldShellClassName(
																Boolean(errors[`goal_target_date_${index}`])
															)}
														>
															<input
																id={`goal-target-date-${index}`}
																type="date"
																value={goal.targetDate}
																min={todayIsoDate()}
																onChange={(event) =>
																	onGoalChange(index, 'targetDate', event.target.value)
																}
																className={fitnessProfileDateInputClassName}
															/>
														</div>
													</div>
												</Field>
												</div>
											</div>
										</div>
									))}
								</div>
								<button
									type="button"
									onClick={addGoal}
									className="mt-4 inline-flex items-center rounded-xl border border-gray-300 dark:border-dark-border px-3.5 py-2 text-sm font-medium text-gray-800 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-bg"
								>
									{formData.goals.length === 0 ? '+ Add a goal' : '+ Add Another Goal'}
								</button>
							</SectionCard>
						)}

						{stepId === 'review' && (
							<SectionCard
								title={isCompleteMode ? 'Review and Save Profile' : 'Review and Create Profile'}
								subtitle={
									isCompleteMode
										? 'Confirm details and finish your profile setup.'
										: 'Confirm details and create your fitness profile.'
								}
							>
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<div className="rounded-xl border border-gray-300 dark:border-dark-border bg-gray-50 dark:bg-dark-bg p-4">
										<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
											Basic profile
										</p>
										<ul className="mt-2 space-y-1 text-sm text-gray-700 dark:text-dark-text">
											<li>Name: {formData.name}</li>
											<li>
												Date of birth: {formData.dateOfBirth}
												{computedAge != null ? ` (age ${computedAge})` : ''}
											</li>
											<li>Height: {formData.height}</li>
											<li>Weight: {formData.weight} lbs</li>
											<li>Blood Pressure: {formData.bloodPressure || 'Not provided'}</li>
										</ul>
									</div>
									<div className="rounded-xl border border-gray-300 dark:border-dark-border bg-gray-50 dark:bg-dark-bg p-4">
										<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
											Current habits
										</p>
										<ul className="mt-2 space-y-1 text-sm text-gray-700 dark:text-dark-text">
											<li>
												Diet: {labelForOption(DIET_PATTERN_OPTIONS, formData.dietPattern)}
											</li>
											<li>
												Exercise:{' '}
												{labelForOption(EXERCISE_FREQUENCY_OPTIONS, formData.exerciseFrequency)}
											</li>
											<li>
												Intensity:{' '}
												{labelForOption(EXERCISE_INTENSITY_OPTIONS, formData.exerciseIntensity)}
											</li>
											<li>Sleep Avg: {formData.sleepHours} hours</li>
										</ul>
									</div>
								</div>
								<div className="mt-4 rounded-xl border border-gray-300 dark:border-dark-border bg-gray-50 dark:bg-dark-bg p-4">
									<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
										Equipment & settings
									</p>
									<ul className="mt-2 space-y-1 text-sm text-gray-700 dark:text-dark-text">
										<li>
											Equipment: {formData.equipmentAccess.trim() || 'Not provided'}
										</li>
										{isHouseholdProfileCreate && (
											<li>
												Hubs:{' '}
												{formData.fitnessHubsOnly
													? 'Fitness Hub and Nutrition Lab only'
													: 'All hubs visible'}
											</li>
										)}
									</ul>
								</div>
								<div className="mt-4 rounded-xl border border-gray-300 dark:border-dark-border bg-gray-50 dark:bg-dark-bg p-4">
									<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
										Goals
									</p>
									<div className="mt-2 space-y-2">
										{formData.goals.filter(
											(goal) =>
												goal.outcome.trim() ||
												goal.targetMetric.trim() ||
												goal.targetDate
										).length === 0 ? (
											<p className="text-sm text-gray-600 dark:text-gray-400">None added</p>
										) : (
											formData.goals
												.filter(
													(goal) =>
														goal.outcome.trim() ||
														goal.targetMetric.trim() ||
														goal.targetDate
												)
												.map((goal, index) => (
												<div
													key={`review-goal-${index + 1}`}
													className="rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card px-3 py-2.5"
												>
													<p className="text-sm font-medium text-gray-900 dark:text-dark-text">
														{index + 1}. {goal.outcome}
													</p>
													<p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
														{labelForGoalCategory(goal.category)} · Target: {goal.targetMetric}
													</p>
													<p className="text-xs text-gray-600 dark:text-gray-400">
														Target date: {goal.targetDate}
													</p>
												</div>
											))
										)}
									</div>
								</div>
							</SectionCard>
						)}

						{submitError && (
							<p className={fitnessProfileFormErrorClassName}>
								{submitError}
							</p>
						)}

						<div className={`sticky bottom-4 z-20 ${fitnessProfileSurfaceCardClassName} p-3`}>
							<div className="flex items-center justify-between gap-3">
								<button
									type="button"
									onClick={goToPreviousStep}
									disabled={currentStep === 0 || isSubmitting}
									className="inline-flex min-w-28 items-center justify-center rounded-xl border border-gray-300 dark:border-dark-border px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-bg disabled:opacity-50"
								>
									Back
								</button>
								<button
									type="submit"
									disabled={isSubmitting}
									className="inline-flex min-w-44 items-center justify-center rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500"
								>
									{isSubmitting
										? isCompleteMode
											? 'Saving…'
											: 'Creating…'
										: isInviteOnlyCreate
											? 'Send Invitation'
											: isCompleteMode
												? 'Complete Profile'
												: isReviewStep
													? 'Create Profile'
													: 'Continue'}
								</button>
							</div>
						</div>
					</form>
				)}
			</div>
		</div>
	);
}
